import { useTranslation } from "react-i18next";
import {
  Folder,
  HardDrive,
  LayoutDashboard,
  Menu,
  Moon,
  ShieldCheck,
  Sun,
  Trash2,
  UserCog,
  Users,
} from "lucide-react";
import { lazy, Suspense, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router";
import { isAdmin } from "@/entities/user";
import { useLogout, useSession } from "@/features/auth";
import { SearchBox } from "@/features/search";
import { StorageUsage } from "@/features/storage-usage";
import { cn } from "@/shared/lib/cn";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  ErrorBoundary,
} from "@/shared/ui";
import { setTheme, useTheme } from "@/app/providers/theme";
import { ShortcutsHelp } from "./ShortcutsHelp";

// The upload queue UI, engine and transport load after first paint.
const UploadPanel = lazy(() =>
  import("@/features/upload").then((m) => ({ default: m.UploadPanel })),
);

const linkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    "flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-150",
    isActive
      ? "bg-primary/10 text-primary shadow-2xs font-semibold"
      : "text-muted hover:bg-surface-subtle hover:text-fg",
  );

function BrandLogo() {
  return (
    <div className="flex items-center gap-2.5 px-3 py-2">
      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-tr from-primary to-blue-500 text-white shadow-xs">
        <Folder aria-hidden size={17} className="fill-current" />
      </div>
      <span className="text-sm font-bold tracking-tight text-fg">
        Navigo <span className="font-semibold text-primary">Drive</span>
      </span>
    </div>
  );
}

function Nav({ admin, onNavigate }: { admin: boolean; onNavigate?: () => void }) {
  const { t } = useTranslation();
  return (
    <nav aria-label={t("nav.main")} className="flex flex-col gap-1">
      <NavLink to="/" end className={linkClass} onClick={onNavigate}>
        <LayoutDashboard aria-hidden size={17} className="shrink-0" />
        <span>{t("nav.dashboard")}</span>
      </NavLink>
      <NavLink to="/drive" className={linkClass} onClick={onNavigate}>
        <HardDrive aria-hidden size={17} className="shrink-0" />
        <span>{t("nav.myFiles")}</span>
      </NavLink>
      <NavLink to="/shared" className={linkClass} onClick={onNavigate}>
        <Users aria-hidden size={17} className="shrink-0" />
        <span>{t("nav.shared")}</span>
      </NavLink>
      <NavLink to="/trash" className={linkClass} onClick={onNavigate}>
        <Trash2 aria-hidden size={17} className="shrink-0" />
        <span>{t("nav.trash")}</span>
      </NavLink>
      {admin ? (
        <>
          <p className="mt-4 px-3 text-[11px] font-semibold uppercase tracking-wider text-muted">
            {t("nav.admin")}
          </p>
          <NavLink to="/admin/users" className={linkClass} onClick={onNavigate}>
            <UserCog aria-hidden size={17} className="shrink-0" />
            <span>{t("nav.users")}</span>
          </NavLink>
          <NavLink to="/admin/audit" className={linkClass} onClick={onNavigate}>
            <ShieldCheck aria-hidden size={17} className="shrink-0" />
            <span>{t("nav.audit")}</span>
          </NavLink>
        </>
      ) : null}
    </nav>
  );
}

function ThemeToggle() {
  const { t } = useTranslation();
  const theme = useTheme();
  const dark =
    theme === "dark" ||
    (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
  return (
    <Tooltip label={dark ? t("nav.themeLight") : t("nav.themeDark")}>
      <Button
        variant="ghost"
        size="icon"
        aria-label={dark ? t("nav.themeLight") : t("nav.themeDark")}
        onClick={() => setTheme(dark ? "light" : "dark")}
        className="rounded-full"
      >
        {dark ? <Sun aria-hidden size={17} /> : <Moon aria-hidden size={17} />}
      </Button>
    </Tooltip>
  );
}

export function AppShell() {
  const { t } = useTranslation();
  const session = useSession();
  const logout = useLogout();
  const navigate = useNavigate();
  const [drawer, setDrawer] = useState(false);
  const admin = isAdmin(session.data);

  const signOut = () =>
    logout.mutate(undefined, { onSettled: () => void navigate("/login", { replace: true }) });

  const initial = session.data?.email ? session.data.email[0]?.toUpperCase() : "U";

  return (
    <div className="flex min-h-screen bg-bg">
      <ShortcutsHelp />
      <ErrorBoundary where="upload-panel" fallback={() => null}>
        <Suspense fallback={null}>
          <UploadPanel userId={session.data?.id} />
        </Suspense>
      </ErrorBoundary>

      <aside className="hidden w-64 shrink-0 flex-col justify-between border-r border-border bg-surface/50 p-3.5 backdrop-blur-xs md:flex">
        <div className="flex flex-col gap-5">
          <BrandLogo />
          <Nav admin={admin} />
        </div>
        <StorageUsage isAdmin={admin} />
      </aside>

      <Dialog open={drawer} onOpenChange={setDrawer}>
        <DialogContent
          aria-describedby={undefined}
          className="left-0 top-0 h-full w-64 max-w-[85vw] translate-x-0 translate-y-0 rounded-none border-r border-border bg-surface p-4"
        >
          <div className="mb-4">
            <BrandLogo />
            <DialogTitle className="sr-only">{t("nav.menu")}</DialogTitle>
          </div>
          <Nav admin={admin} onNavigate={() => setDrawer(false)} />
          <div className="mt-auto pt-6">
            <StorageUsage isAdmin={admin} />
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center gap-2.5 border-b border-border bg-bg/85 px-4 py-2.5 backdrop-blur-md">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            aria-label={t("nav.openMenu")}
            onClick={() => setDrawer(true)}
          >
            <Menu aria-hidden size={18} />
          </Button>
          <SearchBox />
          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  aria-label={t("nav.account")}
                  className="h-9 gap-2 rounded-full px-2 hover:bg-surface"
                >
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary">
                    {initial}
                  </div>
                  <span className="hidden max-w-44 truncate text-xs font-medium text-fg sm:inline">
                    {session.data?.email}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => void navigate("/settings/security")}>
                  {t("nav.security")}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={signOut}>{t("nav.signOut")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="min-w-0 flex-1 p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
